import { observer } from "mobx-react";
import React from "react";
import { useInjection } from "inversify-react";
import { AppController } from "@dropDesk/presentation/app/app.controller";
import { ICONS_DROPDESK } from "@dropDesk/storage/icons/icons.d";
import { hexToRGBA } from "@dropDesk/utils/helpers/colors";
import { SharedColors } from "@dropDesk/domain/entities/theme/app_colors";
import {
    ComparativeMetricsGroup
} from "@dropDesk/domain/entities/reports/report_ticket_comparative/report_ticket_comparative.entity";
import {
    ComparativeMetric,
    ComparativeNormalization
} from "@dropDesk/domain/entities/reports/report_ticket_comparative/comparative_metric.entity";
import MetricCard from "./metric_card";
import { MetricsGrid } from "./styled";

type MetricCardConfig = {
    key: keyof ComparativeMetricsGroup;
    title: string;
    formatValue: (value: number) => string;
    icon: string;
    iconColor: string;
    iconBackground: string;
    hasSecondaryMetric?: boolean;
};

const formatInteger = (value: number): string => {
    return Math.round(value).toLocaleString('pt-BR');
};

const formatDecimal = (value: number): string => {
    return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatRating = (value: number): string => {
    return `${formatDecimal(value)}`;
};

/** Metrics arrive in minutes — same format as report summary text. */
const formatMinutesToComparativeLabel = (totalMinutes: number): string => {
    if (!totalMinutes || totalMinutes <= 0) {
        return '0 min';
    }
    const total = Math.round(totalMinutes);
    const hours = Math.floor(total / 60);
    const mins = total % 60;
    if (hours === 0) {
        return `${mins} min`;
    }
    if (mins === 0) {
        return `${hours}h`;
    }
    return `${hours}h ${mins}min`;
};

const getAveragePerUnitTitle = (normalization: ComparativeNormalization): string => {
    if (normalization === 'hourly') {
        return 'Média por hora';
    }
    return 'Média por dia';
};

const MetricsCards = observer(({
    metrics,
    normalization,
    withComparative = true,
    loading = false,
}: {
    metrics?: ComparativeMetricsGroup | null;
    normalization: ComparativeNormalization;
    withComparative?: boolean;
    loading?: boolean;
}) => {
    const appController = useInjection(AppController);
    const colors = appController.theme.colorScheme;

    const accentSoft = hexToRGBA(colors.accent, 0.12);
    const rating = SharedColors.rate;
    const ratingSoft = hexToRGBA(rating, 0.15);
    const errorColor = colors.textError;
    const errorColorSoft = hexToRGBA(colors.errorOnPrimary, 0.15);

    const cards: MetricCardConfig[] = [
        {
            key: 'timeFirstResponse',
            title: 'Tempo médio de primeira resposta',
            formatValue: formatMinutesToComparativeLabel,
            icon: ICONS_DROPDESK.chatEmpty,
            iconColor: colors.accent,
            iconBackground: accentSoft,
        },
        {
            key: 'timeResolution',
            title: 'Tempo médio de resolução',
            formatValue: formatMinutesToComparativeLabel,
            icon: ICONS_DROPDESK.clock,
            iconColor: SharedColors.textOpenStatus,
            iconBackground: SharedColors.onOpenStatus,
        },
        {
            key: 'totalTickets',
            title: 'Total de atendimentos',
            formatValue: formatInteger,
            icon: ICONS_DROPDESK.headset,
            iconColor: SharedColors.textAttendingStatus,
            iconBackground: SharedColors.onAttendingStatus,
            hasSecondaryMetric: true,
        },
        {
            key: 'averageRating',
            title: 'Satisfação média',
            formatValue: formatRating,
            icon: ICONS_DROPDESK.star,
            iconColor: rating,
            iconBackground: ratingSoft,
        },
        {
            key: 'ticketsInBusinessHour',
            title: 'Atendimentos em horário comercial',
            formatValue: formatInteger,
            icon: ICONS_DROPDESK.business,
            iconColor: SharedColors.textCloseStatus,
            iconBackground: SharedColors.onCloseStatus,
        },
        {
            key: 'ticketsOutBusinessHour',
            title: 'Atendimentos fora do horário comercial',
            formatValue: formatInteger,
            icon: ICONS_DROPDESK.moon,
            iconColor: errorColor,
            iconBackground: errorColorSoft,
        },
    ];

    return (
        <MetricsGrid>
            {cards.map((card) => {
                const metric: ComparativeMetric | undefined = metrics?.[card.key];
                const secondaryMetric = card.hasSecondaryMetric && metrics
                    ? {
                        label: getAveragePerUnitTitle(normalization),
                        metric: metrics.averagePerUnit,
                        formatValue: formatDecimal,
                    }
                    : card.hasSecondaryMetric
                        ? {
                            label: getAveragePerUnitTitle(normalization),
                            metric: new ComparativeMetric({
                                current: 0,
                                previous: 0,
                                variationPercent: 0,
                                direction: 'stable',
                                isImprovement: false,
                            }),
                            formatValue: formatDecimal,
                        }
                        : undefined;

                return (
                    <MetricCard
                        key={card.key}
                        title={card.title}
                        metric={metric}
                        formatValue={card.formatValue}
                        icon={card.icon}
                        iconColor={card.iconColor}
                        iconBackground={card.iconBackground}
                        secondaryMetric={secondaryMetric}
                        withComparative={withComparative}
                        loading={loading || !metric}
                    />
                );
            })}
        </MetricsGrid>
    );
});

export default MetricsCards;
